import { type EnumContainerOrderAction } from '@Model';
import { Badge, Button, iconSizeXs, useTranslate } from '@synqo/frontend-component-library';
import { IconCheck } from '@tabler/icons-react';
import styles from './ContainerActions.module.css';
import { containerLineTexts, type ContainerLineState } from './lineConfirmation';

export interface ContainerLineConfirmationProps {
    action: EnumContainerOrderAction | 'swap';
    state: ContainerLineState;
    completing: boolean;
    canConfirm: boolean;
    locked: boolean;
    blockedBy?: string;
    count?: number;
    onConfirm: () => void;
    onUnconfirm: () => void;
}

/** The status column of a line: confirm it or take the confirmation back. Empty once the line is booked. */
// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function ContainerLineConfirmation({
    action,
    state,
    completing,
    canConfirm,
    locked,
    blockedBy,
    count,
    onConfirm,
    onUnconfirm,
}: ContainerLineConfirmationProps) {
    const i18n = useTranslate('');
    const texts = containerLineTexts(action);
    const counted = (text: string) => (count === undefined ? text : `${count} ${text}`);

    if (state === 'booked') {
        return undefined;
    }

    const confirmed = counted(i18n(texts.confirmed));

    if (state === 'confirmed' && !canConfirm) {
        return <Badge variant="default">{confirmed}</Badge>;
    }

    if (state === 'confirmed') {
        return (
            <Button
                appearance="outlined"
                size="xs"
                className={styles.confirmedChip}
                nohover
                tooltip={i18n('Bestätigung zurücknehmen')}
                locked={locked}
                onClick={onUnconfirm}
            >
                <IconCheck size={iconSizeXs} />
                &nbsp;{confirmed}
            </Button>
        );
    }

    if (!completing || !canConfirm) {
        return undefined;
    }

    return (
        <Button
            appearance="outlined"
            size="xs"
            className={styles.confirmButton}
            nohover
            tooltip={blockedBy}
            locked={locked || blockedBy !== undefined}
            onClick={onConfirm}
        >
            <IconCheck size={iconSizeXs} />
            &nbsp;{counted(i18n(texts.confirm))}
        </Button>
    );
}
